// Vuex `readings` module.
//
// Owns ALL meter-reading presentation logic (filtering, register mapping, summary columns,
// row styling, flag labels) that previously lived in the
// src/mixins/meterInfo/readings/{readings,elecReadings,gasReadings}.js mixins (Constitution I:
// state owns logic, components display only).
//
// Filter state (meterSerialFilter / showOnlyCustomer / showExcludedReadings / readingsToggle)
// currently lives in the `meters` module and is committed there by Readings.vue. To avoid a
// second source of truth during the phased migration, the getters here READ-THROUGH from
// rootState.meters / rootGetters.meterSerialFilter. The local state + mutations below are the
// final-ownership target and are harmless mirrors for now.
//
// DTOs are PascalCase (Reads[].ReadId, ReadTypeFlag). Final-state row styling is driven SOLELY
// by ReadTypeFlag per spec §6 (FR-009). During the transitional shadow-validation window
// (FR-018–FR-022) the legacy date-matching derivation is retained here ONLY to validate
// QueenBee's flag and is removed at sign-off — see the "TRANSITIONAL" sections below.

import moment from 'moment';

// --- spec §6 canonical flag mapping -----------------------------------------

const FLAG_MAP = {
  CoTStart: { label: 'CoT Start', class: 'cotReading' },
  CoTEnd: { label: 'CoT End', class: 'cotReading' },
  CoSStart: { label: 'CoS Start', class: 'cosReading' },
  CoSEnd: { label: 'CoS End', class: 'cosReading' },
  Installed: { label: 'Installed', class: 'mexReading' },
  Removed: { label: 'Removed', class: 'mexReading' },
};

export function flagFor(reading) {
  if (!reading || reading.ReadTypeFlag === null || reading.ReadTypeFlag === undefined) {
    return { label: '', class: '' };
  }
  const mapped = FLAG_MAP[reading.ReadTypeFlag];
  if (mapped) return mapped;
  // Forward-compatibility: unknown enum member renders its raw value, no class/background.
  return { label: String(reading.ReadTypeFlag), class: '' };
}

// Legacy category (CSS class) → the human industry label the flag "used to" read. Per
// Amendment A4 this label is surfaced as the pill's hover/alt (tooltip) text so a user can see
// what the legacy JavaScript calculation produced alongside the rendered QueenBee value.
const LEGACY_FLAG_LABEL = {
  cosReading: 'COS',
  cotReading: 'COT',
  mexReading: 'MEX',
};

export function legacyFlagLabelFor(category) {
  return LEGACY_FLAG_LABEL[category] || '';
}

// --- TRANSITIONAL shadow-mode legacy flag derivation (FR-018–FR-022) ---------
//
// Ported from src/mixins/meterInfo/readings/readings.js (`getRowStyle` + the `MEXs`/`COTs`/
// `COSs` computed properties) so the mixins can be deleted in Phase 5 without losing the
// validation capability. This is retained ONLY to validate QueenBee's `ReadTypeFlag` against
// the proven client-side logic during the cutover window, and MUST be removed at sign-off
// (FR-022). Honeycomb never persists or transmits this computed value — it only drives the
// transitional comparison/log + the rendered (legacy) row class during the shadow window.

export function computeLegacyFlagCategories(service) {
  const activeService = service || {};
  const meters = activeService.Meters || [];
  const tenancies = activeService.Tenancies || [];

  const MEXs = [];
  meters.forEach((meter) => {
    (meter.MeterConfigs || []).forEach((cfg) => {
      if (Object.prototype.hasOwnProperty.call(cfg, 'EffectiveFrom')) {
        MEXs.push(moment(cfg.EffectiveFrom).format('YYYY.MM.DD'));
      }
      if (Object.prototype.hasOwnProperty.call(cfg, 'EffectiveTo')) {
        MEXs.push(moment(cfg.EffectiveTo).format('YYYY.MM.DD'));
      }
    });
  });

  const COTs = tenancies
    .filter((t) => t.AcquisitionType === 'Transferred')
    .map((t) => moment(t.EffectiveFrom).format('YYYY.MM.DD'));

  const COSs = tenancies
    .filter((t) => t.AcquisitionType === 'Registered' || t.ReleaseType === 'Lost')
    .map((t) => {
      const date =
        t.ReleaseType === 'Lost'
          ? moment(t.EffectiveTo).add(1, 'days')
          : moment(t.EffectiveFrom);
      return date.format('YYYY.MM.DD');
    });

  return { MEXs: Array.from(new Set(MEXs)), COTs, COSs };
}

// Reproduces the legacy `getRowStyle` precedence (CoS → CoT → MEX) at category level.
export function legacyFlagCategoryFor(reading, categories) {
  if (!reading) return '';
  const cats = categories || { COSs: [], COTs: [], MEXs: [] };
  const date = moment(reading.ReadingDate).format('YYYY.MM.DD');
  if ((cats.COSs || []).includes(date)) return 'cosReading';
  if ((cats.COTs || []).includes(date)) return 'cotReading';
  if ((cats.MEXs || []).includes(date)) return 'mexReading';
  return '';
}

// Category (CSS class) that QueenBee's ReadTypeFlag maps to, per spec §6.
export function queenBeeFlagCategoryFor(reading) {
  return flagFor(reading).class;
}

// Decorate display rows for the Amendment A4 presentation: the QueenBee `ReadTypeFlag` category
// drives the rendered row class (`_tableClass`, what shows), and the legacy-derived value is
// surfaced as `_legacyFlagLabel` for the pill's hover/alt (tooltip) text — i.e. the REVERSE of
// the original shadow window (FR-019/FR-020 as amended by A4). A category-level mismatch is still
// `console.log`ged (FR-021) for the extended validation period; the legacy calculation is RETAINED
// (not removed) until QueenBee's flag is proven in production. `log` is injected for testability
// and defaults to a no-op.
export function decorateWithShadowComparison(readings, categories, log) {
  const logger = typeof log === 'function' ? log : function () {};
  return (readings || []).map((reading) => {
    if (!reading) return reading;

    const legacyCategory = legacyFlagCategoryFor(reading, categories);
    const queenBeeCategory = queenBeeFlagCategoryFor(reading);
    const queenBeeFlag =
      reading.ReadTypeFlag === null || reading.ReadTypeFlag === undefined
        ? ''
        : String(reading.ReadTypeFlag);

    if (legacyCategory !== queenBeeCategory) {
      logger('[ReadTypeFlag shadow mismatch]', {
        meterPointSharedId: reading.MeterPointSharedId || reading.Mprn || '',
        readId: reading.Id || reading.SharedId || '',
        readingDate: reading.ReadingDate || '',
        legacyCategory: legacyCategory || '(none)',
        queenBeeFlag: queenBeeFlag || '(none)',
        queenBeeCategory: queenBeeCategory || '(none)',
      });
    }

    // A4: render QueenBee's category (`_tableClass`); expose the legacy industry label as the
    // pill tooltip / alt text (`_legacyFlagLabel`).
    return {
      ...reading,
      _tableClass: queenBeeCategory,
      _legacyFlagLabel: legacyFlagLabelFor(legacyCategory),
    };
  });
}

// --- pure reading-derivation helpers (ported from the mixins) ----------------

export function filterByExcludedReadings(readings) {
  return (readings || []).filter((reading) => {
    if (
      (!reading.AssociatedInvoices || reading.AssociatedInvoices.length === 0) &&
      reading.DoNotBill
    ) {
      return false;
    }
    return true;
  });
}

export function filterByMeterSerial(readings, serial) {
  return (readings || []).filter((reading) => reading.Serial === serial);
}

export function filterByCustomer(readings, customerTenancies) {
  if (!customerTenancies || !customerTenancies.length) return [];

  return (readings || []).filter((r) =>
    customerTenancies.some((t) =>
      moment(r.ReadingDate).isBetween(
        t.EffectiveFrom,
        t.ReleaseType === 'Lost' ? moment(t.EffectiveTo).add(1, 'day') : t.EffectiveTo,
        'days',
        '[]'
      )
    )
  );
}

export function deduplicateRegisterReadings(registerReadings) {
  if (!registerReadings || registerReadings.length === 0) return [];

  const uniqueReadings = new Map();

  registerReadings.forEach((reading) => {
    const key = `${reading.MeterRegisterId}-${reading.Reading}`;

    if (!uniqueReadings.has(key)) {
      uniqueReadings.set(key, reading);
    } else {
      const existing = uniqueReadings.get(key);
      if (reading.IsValid && !existing.IsValid) {
        uniqueReadings.set(key, reading);
      }
    }
  });

  return Array.from(uniqueReadings.values());
}

export function computeFilteredReadings(input) {
  const {
    readings,
    showExcludedReadings,
    meterSerialFilter,
    showOnlyCustomer,
    customerTenancies,
  } = input;

  let result = readings || [];

  result = showExcludedReadings ? result : filterByExcludedReadings(result);
  result = meterSerialFilter === 'All' ? result : filterByMeterSerial(result, meterSerialFilter);
  result = showOnlyCustomer ? filterByCustomer(result, customerTenancies) : result;

  result = result.map((reading) => ({
    ...reading,
    RegisterReadings: deduplicateRegisterReadings(reading.RegisterReadings),
  }));

  return result;
}

const BILLING_TYPE_ORDER = { Unrestricted: 0, Day: 1, Night: 2, EveWeekend: 3 };

export function computeMeterConfigs(meters, meterSerialFilter) {
  const configs = [];
  (meters || [])
    .filter((meter) => meter.Serial === meterSerialFilter)
    .forEach((meter) => {
      (meter.MeterConfigs || []).forEach((config) => {
        if (!configs.includes(config)) configs.push(config);
      });
    });
  return configs;
}

export function computeRegisters(meterConfigs) {
  return (meterConfigs || [])
    .flatMap((config) =>
      (config.Registers || []).map((register) => ({
        ...register,
        MeterConfigSharedId: config.SharedId,
      }))
    )
    .filter((r) => r.IsSettlement && r.BillingTypeId !== 'NotApplicable')
    .sort((a, b) => BILLING_TYPE_ORDER[a.BillingTypeId] - BILLING_TYPE_ORDER[b.BillingTypeId]);
}

export function computeRegisterHeaders(registers) {
  const headers = {};
  (registers || []).forEach((register) => {
    headers[`${register.RegisterId}:${register.BillingTypeId}:${register.Tpr}`] = {
      label: `${register.RegisterId} - ${register.BillingTypeId} (${register.Tpr})`,
      class: 'text-center',
    };
  });
  return headers;
}

export function computeMappedReadings(filteredReadings, meterConfigs) {
  return (filteredReadings || []).map((reading) => {
    const config = (meterConfigs || []).find(
      (c) => c.SharedId === reading.MeterConfigSharedId
    );

    if (config) {
      return {
        ...reading,
        RegisterReadings: reading.RegisterReadings.map((rr) => {
          const register = (config.Registers || []).find(
            (r) => r.RegisterId === rr.MeterRegisterId
          );
          if (register) {
            return { ...rr, BillingTypeId: register.BillingTypeId, Tpr: register.Tpr };
          }
          return undefined;
        }),
      };
    }
    return undefined;
  });
}

// --- summary column definitions (ported from the elec/gas mixins) ------------

const GAS_SUMMARY_FIELDS = {
  ReadingDate: { label: 'Date', sortable: true, class: 'text-center' },
  Serial: { label: 'Meter Serial', class: 'text-center' },
  ReadingType: { label: 'Reading Type', class: 'text-center' },
  MeterReading: { label: 'Reading', class: 'text-center' },
  AssociatedInvoices: { label: 'Billing', class: 'text-center' },
};

const GAS_SUMMARY_CONVERTER_FIELDS = {
  ReadingDate: { label: 'Date', sortable: true, class: 'text-center' },
  Serial: { label: 'Meter Serial', class: 'text-center' },
  ReadingType: { label: 'Reading Type', class: 'text-center' },
  MeterReading: { label: 'Reading', class: 'text-center' },
  ConverterUncorrectedReading: { label: 'Uncorrected Reading', class: 'text-center' },
  ConverterCorrectedReading: { label: 'Corrected Reading', class: 'text-center' },
  AssociatedInvoices: { label: 'Billing', class: 'text-center' },
};

export function computeSummaryColumns(fuel, registerHeaders, hasConverter) {
  if (fuel === 'Electricity' || fuel === 'elec') {
    return {
      ReadingDate: { label: 'Date', sortable: true, class: 'text-center' },
      MeterSerial: { label: 'Meter Serial', class: 'text-center' },
      ...(registerHeaders || {}),
      ReadingType: { label: 'Reading Type', class: 'text-center' },
      ValidationStatus: { label: 'Validation', class: 'text-center' },
      AssociatedInvoices: { label: 'Billing', class: 'text-center' },
      ReadingEvent: { label: '', class: 'text-center' },
    };
  }
  return hasConverter ? { ...GAS_SUMMARY_CONVERTER_FIELDS } : { ...GAS_SUMMARY_FIELDS };
}

// --- SchemaTable column/row builders (Phase 5 consolidation) -----------------
// ReadingsTable.vue renders BOTH fuels through the shared SchemaTable using these
// store-built definitions, so the component holds no presentation logic (NFR-002).

function padTo(value, dials) {
  if (value === null || value === undefined) return '';
  return value.toString().padStart(dials || 0, '0');
}

function validationLetter(status) {
  switch (status) {
    case 'Failed':
      return 'F';
    case 'NotValidated':
      return 'U';
    case 'Valid':
      return 'V';
    default:
      return '';
  }
}

function readingTypeText(type) {
  if (!type) return '';

  // Keep the legacy consolidation used by the pre-QueenBee gas/elec tables:
  // Customer/CoT -> C, any Estimate* -> E, Deemed -> D, all other known/unknown values -> A.
  if (type === 'Customer' || type === 'ChangeOfTenancy') return 'C';
  if (type === 'Deemed') return 'D';
  if (type.includes && type.includes('Estimate')) return 'E';
  return 'A';
}

// Legacy ReadingEvent text (elec) helper removed under Amendment A4: the legacy value is now
// surfaced as the Flag pill's hover/alt text (`_legacyFlagLabel`), so the dedicated unlabeled
// elec Reading-Event column is no longer rendered.

export function buildReadingsColumns(fuel, registerHeaders, hasConverter) {
  const isElec = fuel === 'Electricity' || fuel === 'elec';
  const billingColumn = {
    key: 'AssociatedInvoices',
    label: 'Billing',
    component: 'BillingStatusToggle',
    componentProps: { invoicesField: 'AssociatedInvoices', excludeField: 'DoNotBill' },
    align: 'center',
  };
  // Phase 6 (T043/FR-013/FR-014): rightmost Flag column, identical across fuels. The pill is
  // bound to the QueenBee `ReadTypeFlag` (PascalCase, matching the DTO) and maps it to a
  // label + colour class via §6. SchemaTable passes `value = item.ReadTypeFlag`, `row = item`.
  // Per Amendment A4 the pill renders QueenBee's value and shows the legacy-derived value
  // (`row._legacyFlagLabel`) as its hover/alt tooltip.
  const flagColumn = {
    key: 'ReadTypeFlag',
    label: 'Flag',
    component: 'ReadingTypeFlagPill',
    align: 'center',
  };

  // Reading Type column: render plain text (no badge/block styling) and keep the legend on the
  // column header via SchemaTable `headerHelp`.
  const readingTypeHeaderHelp =
    '<div><b>A</b> - Actual Reading</div><br>' +
    '<div><b>C</b> - Customer Reading</div><br>' +
    '<div><b>E</b> - Estimated Reading</div><br>' +
    '<div><b>D</b> - Deemed Reading</div>';
  const readingTypeColumn = {
    key: 'ReadingType',
    label: 'Reading Type',
    formatter: readingTypeText,
    headerHelp: readingTypeHeaderHelp,
    headerHelpTitle: 'Reading Type',
    align: 'center',
  };

  if (isElec) {
    const columns = [
      { key: 'ReadingDate', label: 'Date', formatter: 'shortDate', sortable: true, align: 'center', titleField: '_legacyFlagLabel' },
      { key: 'MeterSerial', label: 'Meter Serial', align: 'center' },
    ];
    Object.keys(registerHeaders || {}).forEach((key) => {
      columns.push({ key, label: registerHeaders[key].label, align: 'center' });
    });
    columns.push(
      readingTypeColumn,
      { key: '_validationDisplay', label: 'Validation', align: 'center' },
      billingColumn,
      flagColumn
    );
    return columns;
  }

  const columns = [
    { key: 'ReadingDate', label: 'Date', formatter: 'shortDate', sortable: true, align: 'center', titleField: '_legacyFlagLabel' },
    { key: 'Serial', label: 'Meter Serial', align: 'center' },
    readingTypeColumn,
    { key: '_meterReadingDisplay', label: 'Reading', align: 'center' },
  ];
  if (hasConverter) {
    columns.push(
      { key: '_converterUncorrectedDisplay', label: 'Uncorrected Reading', align: 'center' },
      { key: '_converterCorrectedDisplay', label: 'Corrected Reading', align: 'center' }
    );
  }
  columns.push(billingColumn, flagColumn);
  return columns;
}

export function buildGasRows(decoratedReadings, meters) {
  const findMeter = (serial) => (meters || []).find((m) => m.Serial === serial) || {};
  const findConfigDials = (reading) => {
    const meter = findMeter(reading.Serial);
    const configs = meter.MeterConfigs || [];
    const config = configs.find((c) => c.SharedId === reading.MeterConfigSharedId);
    return config ? config.Dials : meter.Dials;
  };
  return (decoratedReadings || []).filter(Boolean).map((reading) => {
    const dials = reading.Dials || findConfigDials(reading);
    const primary =
      reading.ConverterCorrectedReading != null
        ? reading.ConverterCorrectedReading
        : reading.MeterReading;
    return {
      ...reading,
      _meterReadingDisplay: padTo(primary, dials),
      _converterUncorrectedDisplay: padTo(reading.ConverterUncorrectedReading, dials),
      _converterCorrectedDisplay: padTo(reading.ConverterCorrectedReading, dials),
    };
  });
}

export function buildElecRows(decoratedReadings, registers) {
  return (decoratedReadings || []).filter(Boolean).map((reading) => {
    const row = {
      ...reading,
      MeterSerial: reading.Serial,
      _validationDisplay: validationLetter(reading.ValidationStatus),
    };
    (registers || []).forEach((register) => {
      const key = `${register.RegisterId}:${register.BillingTypeId}:${register.Tpr}`;
      const matches = (reading.RegisterReadings || []).filter(
        (rr) =>
          rr &&
          rr.MeterRegisterId === register.RegisterId &&
          rr.BillingTypeId === register.BillingTypeId &&
          rr.Tpr === register.Tpr
      );
      row[key] = matches.map((rr) => rr.Reading).join('');
    });
    return row;
  });
}

// --- read-through accessors --------------------------------------------------

function activeService(rootState) {
  return rootState && rootState.meters ? rootState.meters.activeService : null;
}

function effectiveMeterSerialFilter(rootState, rootGetters) {
  if (rootGetters && rootGetters.meterSerialFilter !== undefined) {
    return rootGetters.meterSerialFilter;
  }
  return rootState && rootState.meters ? rootState.meters.meterSerialFilter : 'All';
}

function customerTenanciesFor(rootState) {
  const service = activeService(rootState);
  const tenancies = service && service.Tenancies ? service.Tenancies : [];
  const customer = rootState && rootState.customers ? rootState.customers.customer : null;
  if (!customer) return [];
  return tenancies.filter((t) => t.CustomerReference === customer.CustomerReference);
}

// --- module ------------------------------------------------------------------

export const state = {
  meterSerialFilter: 'All',
  showOnlyCustomer: true,
  showExcludedReadings: false,
  readingsToggle: null,
};

export const mutations = {
  updateMeterSerialFilter(state, filter) {
    state.meterSerialFilter = filter;
  },
  updateShowOnlyCustomer(state, bool) {
    state.showOnlyCustomer = bool;
  },
  updateShowExcludedReadings(state, bool) {
    state.showExcludedReadings = bool;
  },
  updateReadingsToggle(state, item) {
    state.readingsToggle = item;
  },
};

export const getters = {
  filteredReadings(state, getters, rootState, rootGetters) {
    const service = activeService(rootState);
    if (!service) return [];
    return computeFilteredReadings({
      readings: service.Readings,
      showExcludedReadings: rootState.meters.showExcludedReadings,
      meterSerialFilter: effectiveMeterSerialFilter(rootState, rootGetters),
      showOnlyCustomer: rootState.meters.showOnlyCustomer,
      customerTenancies: customerTenanciesFor(rootState),
    });
  },

  registerColumns(state, getters, rootState, rootGetters) {
    const service = activeService(rootState);
    if (!service) return [];
    const meterConfigs = computeMeterConfigs(
      service.Meters,
      effectiveMeterSerialFilter(rootState, rootGetters)
    );
    return computeRegisters(meterConfigs);
  },

  mappedReadings(state, getters, rootState, rootGetters) {
    const service = activeService(rootState);
    if (!service) return [];
    const meterConfigs = computeMeterConfigs(
      service.Meters,
      effectiveMeterSerialFilter(rootState, rootGetters)
    );
    return computeMappedReadings(getters.filteredReadings, meterConfigs);
  },

  summaryColumns(state, getters) {
    return (fuel, hasConverter) => {
      const registerHeaders = computeRegisterHeaders(getters.registerColumns);
      return computeSummaryColumns(fuel, registerHeaders, hasConverter);
    };
  },

  hasConverter(state, getters, rootState) {
    const service = activeService(rootState);
    const meters = service && service.Meters ? service.Meters : [];
    if (!meters.length) return false;
    const current = meters.reduce((latest, m) => (latest.Installed > m.Installed ? latest : m));
    return Object.prototype.hasOwnProperty.call(current, 'ConverterSerial');
  },

  // Phase 5: SchemaTable column array per fuel (logic-free component consumes this).
  readingsTableColumns(state, getters) {
    return (fuel, hasConverter) => {
      const registerHeaders = computeRegisterHeaders(getters.registerColumns);
      return buildReadingsColumns(fuel, registerHeaders, hasConverter);
    };
  },

  // Phase 5: SchemaTable data rows per fuel, sourced from the shadow-decorated rows so the
  // transitional console.log/tooltip (FR-018–FR-021) is preserved, with display-only fields
  // (padded readings, validation letter, reading-event text) precomputed here.
  readingsTableRows(state, getters, rootState) {
    return (fuel) => {
      const isElec = fuel === 'Electricity' || fuel === 'elec';
      if (isElec) {
        return buildElecRows(getters.shadowFlaggedMappedReadings, getters.registerColumns);
      }
      const service = activeService(rootState);
      const meters = service && service.Meters ? service.Meters : [];
      return buildGasRows(getters.shadowFlaggedReadings, meters);
    };
  },

  rowClassFor() {
    return (reading) => flagFor(reading).class;
  },

  flagLabelFor() {
    return (reading) => flagFor(reading).label;
  },

  // --- shadow-validation getters (FR-018–FR-022, presentation reversed by Amendment A4) -------
  // Per A4 the legacy calculation is RETAINED (not removed at sign-off) until QueenBee's flag is
  // proven in production for several months. `shadowFlaggedReadings` (gas) /
  // `shadowFlaggedMappedReadings` (elec) decorate the display rows with the QueenBee-derived
  // `_tableClass` (what renders) and the legacy industry label `_legacyFlagLabel` (the pill's
  // alt/tooltip), and `console.log` category-level mismatches once per dataset
  // (Vuex caches the getter, so it does not re-run per render).
  legacyFlagCategories(state, getters, rootState) {
    return computeLegacyFlagCategories(activeService(rootState));
  },

  shadowFlaggedReadings(state, getters, rootState) {
    const service = activeService(rootState);
    if (!service) return [];
    return decorateWithShadowComparison(
      getters.filteredReadings,
      computeLegacyFlagCategories(service),
      // eslint-disable-next-line no-console
      console.log
    );
  },

  shadowFlaggedMappedReadings(state, getters, rootState) {
    const service = activeService(rootState);
    if (!service) return [];
    return decorateWithShadowComparison(
      getters.mappedReadings,
      computeLegacyFlagCategories(service),
      // eslint-disable-next-line no-console
      console.log
    );
  },
};

export default {
  state,
  mutations,
  getters,
};
